/**
 * Facoltà del chiamante sui set di agenti: piano, limiti, set posseduti
 * (ADR 0023 §3, `GET /api/v1/agent-sets/capabilities`).
 *
 * Le leggono il guscio (badge del piano, voce «Nuovo set»), la pagina iniziale
 * (dove mandare chi non ha ancora un set) e l'elenco dei set. Una lettura sola,
 * condivisa da un contesto, invece di una per componente.
 *
 * Servono a **non proporre** ciò che il backend rifiuterebbe; il controllo vero
 * resta della creazione, che ripete conteggio e limite e risponde 409
 * `plan_limit_sets` o `plan_limit_projects`.
 */

import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import { useApi } from '../api/ApiContext';
import type { SetCapabilities } from '../api/agents';
import { useResource } from '../api/hooks/useResource';

/** Stato delle facoltà esposto ai componenti. */
export interface SetCapabilitiesState {
  /** Facoltà lette; `null` finché non arrivano o se la lettura è fallita. */
  data: SetCapabilities | null;
  /** True durante la prima lettura. */
  loading: boolean;
  /** Messaggio dell'errore di lettura, `null` se non c'è. */
  error: string | null;
  /** Rilegge le facoltà, per esempio dopo aver creato un set. */
  reload: () => Promise<void>;
}

/** Stato di chi è fuori dal provider: nessuna facoltà nota, niente da rileggere. */
const OUTSIDE: SetCapabilitiesState = {
  data: null,
  loading: false,
  error: null,
  reload: async () => undefined,
};

const SetCapabilitiesContext = createContext<SetCapabilitiesState>(OUTSIDE);

/** Proprietà del provider. */
export interface SetCapabilitiesProviderProps {
  /** Sottoalbero servito. */
  children: ReactNode;
}

/**
 * Legge le facoltà una volta e le espone al sottoalbero.
 *
 * @param props.children Sottoalbero servito.
 * @returns Il provider.
 */
export function SetCapabilitiesProvider({ children }: SetCapabilitiesProviderProps): JSX.Element {
  const { agents } = useApi();
  const load = useCallback(() => agents.getCapabilities(), [agents]);
  const resource = useResource(load);
  const { reload } = resource;
  const value = useMemo<SetCapabilitiesState>(
    () => ({
      data: resource.data,
      loading: resource.loading,
      error: resource.error,
      reload: () => reload({ silent: true }),
    }),
    [resource.data, resource.loading, resource.error, reload],
  );
  return <SetCapabilitiesContext.Provider value={value}>{children}</SetCapabilitiesContext.Provider>;
}

/**
 * Facoltà del chiamante sui set.
 *
 * @returns Lo stato; fuori dal provider, nessuna facoltà nota.
 */
export function useSetCapabilities(): SetCapabilitiesState {
  return useContext(SetCapabilitiesContext);
}
